import { useState } from "react";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { BlockPalette } from "./BlockPalette";
import type { OverlayRect } from "./overlayGeometry";

interface BlockAddMenuProps {
  /** Container-relative geometry of the "+" the menu hangs off. */
  anchorRect: OverlayRect;
  /** The stored document HTML, for the palette's chrome-presence checks. */
  documentHtml: string;
  /** Inserts the chosen block and closes the menu. */
  onInsert: (blockHtml: string) => void;
  /** Closes the menu without inserting. */
  onClose: () => void;
}

/**
 * The block palette as a popover beside the "+" the user clicked. It anchors to
 * a zero-height marker placed at that "+" rather than to the button itself: the
 * button is hover chrome and unmounts the moment the pointer leaves the block,
 * which would take an open menu with it. The marker lives inside the scrolling
 * preview, so the menu tracks the block as the page scrolls. The palette lists
 * every quote field, so it is capped to a menu-sized box and scrolls inside it.
 * It portals into the editor's own dialog rather than the body: the editor
 * opens as a modal, whose scroll lock cancels wheel events everywhere outside
 * the dialog's subtree, which leaves a body-portalled menu with a scrollbar it
 * can only be dragged by.
 * @param anchorRect - Container-relative geometry of the "+"
 * @param documentHtml - The stored document HTML
 * @param onInsert - Inserts the chosen block and closes the menu
 * @param onClose - Closes the menu without inserting
 * @returns The anchored palette popover
 */
export function BlockAddMenu({
  anchorRect,
  documentHtml,
  onInsert,
  onClose,
}: BlockAddMenuProps) {
  const [marker, setMarker] = useState<HTMLDivElement | null>(null);
  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <div
          ref={setMarker}
          className="absolute pointer-events-none"
          style={{
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
            height: anchorRect.height,
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        collisionPadding={12}
        container={marker?.closest<HTMLElement>('[role="dialog"]') ?? undefined}
        className="w-72 overflow-hidden p-0"
      >
        <div className="max-h-96 overflow-y-auto overscroll-contain">
          <BlockPalette documentHtml={documentHtml} onInsert={onInsert} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
